import React from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AdminPartnerProviderConfiguration } from "@/types/admin-partner-provider";
import {
  createPartnerProviderSchema,
  PartnerProviderFormValues,
} from "../utils/partnerProviderSchema";

/** Which fields the user actually touched — drives the partial PUT payload. */
export type PartnerProviderDirtyFields = Partial<
  Record<keyof PartnerProviderFormValues, boolean>
>;

interface PartnerProviderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partner?: AdminPartnerProviderConfiguration | null;
  availableProviders: string[];
  isPending: boolean;
  onSubmit: (
    values: PartnerProviderFormValues,
    dirtyFields: PartnerProviderDirtyFields,
  ) => Promise<void>;
}

interface PartnerProviderDialogFormProps {
  partner?: AdminPartnerProviderConfiguration | null;
  availableProviders: string[];
  isPending: boolean;
  onClose: () => void;
  onSubmit: (
    values: PartnerProviderFormValues,
    dirtyFields: PartnerProviderDirtyFields,
  ) => Promise<void>;
}

export const PartnerProviderDialog: React.FC<PartnerProviderDialogProps> = ({
  open,
  onOpenChange,
  partner,
  availableProviders,
  isPending,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const isEdit = Boolean(partner);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t("partners.admin.dialog.editTitle")
              : t("partners.admin.dialog.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? t("partners.admin.dialog.editDescription")
              : t("partners.admin.dialog.createDescription")}
          </DialogDescription>
        </DialogHeader>
        {open ? (
          <PartnerProviderDialogForm
            key={partner?.id ?? "create"}
            partner={partner}
            availableProviders={availableProviders}
            isPending={isPending}
            onClose={() => onOpenChange(false)}
            onSubmit={onSubmit}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const PartnerProviderDialogForm: React.FC<PartnerProviderDialogFormProps> = ({
  partner,
  availableProviders,
  isPending,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const isEdit = Boolean(partner);

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, dirtyFields },
  } = useForm<PartnerProviderFormValues>({
    resolver: zodResolver(createPartnerProviderSchema()),
    defaultValues: {
      provider: partner?.provider ?? availableProviders[0] ?? "",
      enabled: partner?.enabled ?? true,
      baseUrl: partner?.baseUrl ?? "",
      syncIntervalMinutes: partner?.syncIntervalMinutes ?? 0,
      platformFeePercent: partner?.platformFeePercent ?? 0,
      apiKey: "",
      webhookSecret: "",
      description: partner?.metadata?.description ?? "",
      categories: partner?.metadata?.categories ?? "",
      defaultMaxActivitiesEnabled: partner?.defaultMaxActivitiesEnabled ?? true,
      defaultMaxActivities: partner?.defaultMaxActivities ?? 100,
    },
  });

  const defaultMaxActivitiesEnabled = watch("defaultMaxActivitiesEnabled");

  const handleFormSubmit = async (values: PartnerProviderFormValues) => {
    await onSubmit(values, dirtyFields);
  };

  let submitLabel = t("partners.admin.dialog.add");

  if (isPending) {
    submitLabel = t("partners.admin.dialog.saving");
  } else if (isEdit) {
    submitLabel = t("partners.admin.dialog.save");
  }

  const onFormSubmit = handleSubmit(handleFormSubmit);

  return (
    <form onSubmit={onFormSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="provider">
            {t("partners.admin.dialog.provider")}
          </Label>
          {isEdit ? (
            <Input
              id="provider"
              value={t(`partners.providers.${partner?.provider}`, {
                defaultValue: partner?.provider,
              })}
              disabled
            />
          ) : (
            <>
              <Controller
                name="provider"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-full" id="provider">
                      <SelectValue
                        placeholder={t(
                          "partners.admin.dialog.providerPlaceholder",
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {availableProviders.map((providerOption) => (
                        <SelectItem key={providerOption} value={providerOption}>
                          {t(`partners.providers.${providerOption}`, {
                            defaultValue: providerOption,
                          })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.provider ? (
                <p className="text-destructive text-xs">
                  {errors.provider.message}
                </p>
              ) : null}
            </>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="enabled-switch">
            {t("partners.admin.dialog.enabled")}
          </Label>
          <Controller
            name="enabled"
            control={control}
            render={({ field }) => (
              <div className="flex min-h-10 items-center gap-3 rounded-md border px-4">
                <Switch
                  id="enabled-switch"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <span className="text-foreground text-sm">
                  {field.value
                    ? t("partners.admin.status.enabled")
                    : t("partners.admin.status.disabled")}
                </span>
              </div>
            )}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="baseUrl">{t("partners.admin.dialog.baseUrl")}</Label>
          <Input
            id="baseUrl"
            placeholder={t("partners.admin.dialog.baseUrlPlaceholder")}
            autoComplete="off"
            {...register("baseUrl")}
          />
          {errors.baseUrl ? (
            <p className="text-destructive text-xs">{errors.baseUrl.message}</p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="syncIntervalMinutes">
            {t("partners.admin.dialog.syncIntervalMinutes")}
          </Label>
          <Input
            id="syncIntervalMinutes"
            type="number"
            min={0}
            autoComplete="off"
            {...register("syncIntervalMinutes", { valueAsNumber: true })}
          />
          {errors.syncIntervalMinutes ? (
            <p className="text-destructive text-xs">
              {errors.syncIntervalMinutes.message}
            </p>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="platformFeePercent">
            {t("partners.admin.dialog.platformFeePercent")}
          </Label>
          <Input
            id="platformFeePercent"
            type="number"
            min={0}
            autoComplete="off"
            {...register("platformFeePercent", { valueAsNumber: true })}
          />
          {errors.platformFeePercent ? (
            <p className="text-destructive text-xs">
              {errors.platformFeePercent.message}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="categories">
            {t("partners.admin.dialog.categories")}
          </Label>
          <Input
            id="categories"
            placeholder={t("partners.admin.dialog.categoriesPlaceholder")}
            autoComplete="off"
            {...register("categories")}
          />
          {isEdit ? (
            <p className="text-muted-foreground text-xs">
              {t("partners.admin.dialog.editMetadataHelp")}
            </p>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="apiKey">{t("partners.admin.dialog.apiKey")}</Label>
          <Input
            id="apiKey"
            type="password"
            placeholder={
              isEdit
                ? t("partners.admin.dialog.apiKeyEditPlaceholder")
                : t("partners.admin.dialog.apiKeyPlaceholder")
            }
            autoComplete="new-password"
            {...register("apiKey")}
          />
          <p className="text-muted-foreground text-xs">
            {isEdit
              ? t("partners.admin.dialog.apiKeyEditHelp")
              : t("partners.admin.dialog.apiKeyHelp")}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="webhookSecret">
            {t("partners.admin.dialog.webhookSecret")}
          </Label>
          <Input
            id="webhookSecret"
            type="password"
            placeholder={
              isEdit
                ? t("partners.admin.dialog.webhookSecretEditPlaceholder")
                : t("partners.admin.dialog.webhookSecretPlaceholder")
            }
            autoComplete="new-password"
            {...register("webhookSecret")}
          />
          <p className="text-muted-foreground text-xs">
            {isEdit
              ? t("partners.admin.dialog.webhookSecretEditHelp")
              : t("partners.admin.dialog.webhookSecretHelp")}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="description">
          {t("partners.admin.dialog.description")}
        </Label>
        <Textarea
          id="description"
          placeholder={t("partners.admin.dialog.descriptionPlaceholder")}
          {...register("description")}
        />
        {isEdit ? (
          <p className="text-muted-foreground text-xs">
            {t("partners.admin.dialog.editMetadataHelp")}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 border-t pt-4">
        <h4 className="text-sm font-medium">
          {t("partners.admin.dialog.activityLimitSectionTitle")}
        </h4>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="defaultMaxActivitiesEnabled-switch">
              {t("partners.admin.dialog.defaultMaxActivitiesEnabled")}
            </Label>
            <Controller
              name="defaultMaxActivitiesEnabled"
              control={control}
              render={({ field }) => (
                <div className="flex min-h-10 items-center gap-3 rounded-md border px-4">
                  <Switch
                    id="defaultMaxActivitiesEnabled-switch"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <span className="text-foreground text-sm">
                    {field.value
                      ? t("partners.admin.boolean.yes")
                      : t("partners.admin.boolean.no")}
                  </span>
                </div>
              )}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="defaultMaxActivities">
              {t("partners.admin.dialog.defaultMaxActivities")}
            </Label>
            <Input
              id="defaultMaxActivities"
              type="number"
              min={1}
              max={1000}
              disabled={!defaultMaxActivitiesEnabled}
              autoComplete="off"
              {...register("defaultMaxActivities", { valueAsNumber: true })}
            />
            {errors.defaultMaxActivities ? (
              <p className="text-destructive text-xs">
                {errors.defaultMaxActivities.message}
              </p>
            ) : (
              <p className="text-muted-foreground text-xs">
                {t("partners.admin.dialog.defaultMaxActivitiesHint")}
              </p>
            )}
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isPending}
        >
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={isPending}>
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
};
